import React, { useContext, useState, useEffect } from "react";
import { FaHandsHelping, FaLock } from "react-icons/fa";
import { FaPeopleGroup } from "react-icons/fa6";
import { IoClose } from "react-icons/io5";
import { IoChevronForwardOutline } from "react-icons/io5";
import { UserContext } from "../storage/contexts/UserContext";
import userPlaceholder from "../assets/images/users/user-vector.png";
import FilledButton from "../InterstellrComponents/FilledButton";
import CommonTextField from "../InterstellrComponents/CommonTextField";
import axios from "axios";
import {
  UPDATE_USER_PROFILE,
  UPLOAD_COMPANY_LOGO,
  UPLOAD_PROFILE_PICTURE,
} from "../Routes";
import { generateURLOptions } from "../URLOptions";

const UserProfileModal = ({
  isOpen,
  onClose,
  onLogout,
  onChangePassword,
  onSupportPeople,
  onManageTeam,
  onSave,
}) => {
  const { profile, setProfile } = useContext(UserContext);

  // Form state
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [profileImage, setProfileImage] = useState(null);
  const [imageUploading, setImageUploading] = useState(false);

  // Initialize form values when profile changes or modal opens
  useEffect(() => {
    if (profile) {
      setFirstName(profile?.firstname ?? "");
      setLastName(profile?.lastname ?? "");
      setEmail(profile?.email ?? "");
      setPhone(profile?.mobile ?? "");
      setProfileImage(profile?.profileImage ?? null);
    }
  }, [profile, isOpen]);

  const handleImageUpload = async (e) => {
    if (!e || !e.target.files[0]) return;
    setImageUploading(true);

    const formData = new FormData();
    formData.append("image", e.target.files[0]);

    try {
      const response = await axios.post(UPLOAD_PROFILE_PICTURE, formData, {
        headers: generateURLOptions("multipart/form-data"),
      });

      console.log("UPLOAD RESPONSE: ", JSON.stringify(response.data));

      if (response?.data?.success === false) {
        alert(response?.data?.message ?? "");
        setImageUploading(false);
        return;
      }

      setProfileImage(response.data.data?.profileImage);
      setProfile(response.data.data);
      setImageUploading(false);
    } catch (error) {
      console.error("Profile Image Upload Error: ", error);
      setImageUploading(false);
      return alert(error);
    }
  };

  const handleSave = async () => {
    const response = await axios.post(
      UPDATE_USER_PROFILE,
      {
        firstname: firstName,
        lastname: lastName,
        email: email,
        mobile: phone,
      },
      {
        headers: generateURLOptions(),
      },
    );

    onClose();
  };

  return (
    // Overlay with fade transition
    <div
      className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] transition-opacity duration-300 ${
        isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
      }`}
    >
      {/* Modal Container with scale and fade */}
      <div
        className={`bg-white rounded-xl w-full max-w-md mx-4 p-6 relative shadow-xl transition-all duration-300 max-h-[90vh] overflow-y-auto ${
          isOpen ? "opacity-100 scale-100" : "opacity-0 scale-95"
        }`}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-2 right-2 p-1 hover:bg-gray-100 rounded-full transition-colors"
          aria-label="Close"
        >
          <IoClose size={24} />
        </button>

        {/* Profile Header */}
        <h3 className="text-xl font-semibold text-gray-900 mb-4">
          Edit Profile
        </h3>

        {/* Profile Picture Upload */}
        <div className="flex flex-col items-center mb-6">
          <div className="relative">
            <img
              src={profileImage ?? userPlaceholder}
              className="w-24 h-24 object-cover rounded-full border-2 border-gray-200"
              alt={firstName ?? ""}
            />

            {imageUploading && (
              <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 rounded-full">
                <div className="h-8 w-8 rounded-full border-4 border-white/30 border-t-white animate-spin" />
              </div>
            )}
          </div>

          <button
            onClick={() =>
              document.getElementById("profileImageUpload").click()
            }
            className="mt-3 px-4 py-2 text-sm font-medium text-mpPurple hover:text-mpPurple/80 transition-colors"
            disabled={imageUploading}
          >
            {imageUploading ? "Uploading..." : "Change Profile Picture"}
          </button>

          <input
            id="profileImageUpload"
            type="file"
            onChange={handleImageUpload}
            accept="image/*"
            className="hidden"
          />
        </div>

        {/* Form Fields */}
        <div className="space-y-2 mb-2">
          <CommonTextField
            title="First Name"
            value={firstName}
            onChange={setFirstName}
          />

          <CommonTextField
            title="Last Name"
            value={lastName}
            onChange={setLastName}
          />

          <CommonTextField title="Email" value={email} onChange={setEmail} />

          <CommonTextField
            title="Phone Number"
            value={phone}
            onChange={setPhone}
          />

          <div>
            <p className="text-sm font-medium text-gray-700 mb-1">
              Support Code
            </p>
            <p className="text-base font-semibold text-black px-3 py-2 bg-gray-50 rounded-lg border border-gray-200">
              {profile?.supportCode ?? ""}
            </p>
          </div>
        </div>

        {/* Menu Options */}
        {/* Support People */}
        {profile?.usertype != 3 ? (
          <div
            onClick={onSupportPeople}
            className="cursor-pointer flex flex-row justify-start items-center mt-2 py-2 px-3 border border-slate-300 rounded-lg"
          >
            <FaHandsHelping size={20} className="text-mpPurple" />

            <p className="text-sm font-medium text-black ml-2">
              Configure Support People
            </p>

            <div style={{ flex: 1 }} />
            <IoChevronForwardOutline />
          </div>
        ) : null}

        {/* Manage Team */}
        {profile?.usertype == 1 ? (
          <div
            onClick={onManageTeam}
            className="cursor-pointer flex flex-row justify-start items-center mt-2 py-2 px-3 border border-slate-300 rounded-lg"
          >
            <FaPeopleGroup size={20} className="text-mpPurple" />

            <p className="text-sm font-medium text-black ml-2">Manage Team</p>

            <div style={{ flex: 1 }} />
            <IoChevronForwardOutline />
          </div>
        ) : null}

        <div
          onClick={onChangePassword}
          className="cursor-pointer flex flex-row justify-start items-center mt-2 mb-2 py-2 px-3 border border-slate-300 rounded-lg"
        >
          <FaLock size={16} className="text-mpPurple" />

          <p className="text-sm font-medium text-black ml-3">Change Password</p>

          <div style={{ flex: 1 }} />
          <IoChevronForwardOutline />
        </div>

        <div className="mb-2">
          <FilledButton text="Save Changes" onPress={handleSave} />
        </div>

        <FilledButton text="Logout" onPress={onLogout} />
      </div>
    </div>
  );
};

export default UserProfileModal;
